export interface ApiError {
  status: number;
  error?: string;
  message: string;
  details?: unknown;
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.headers) {
    const incoming = opts.headers as Record<string, string>;
    for (const [k, v] of Object.entries(incoming)) headers[k] = v;
  }
  if (opts.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(path, { ...opts, credentials: "include", headers });
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const obj = (body ?? {}) as Record<string, unknown>;
    const apiErr: ApiError = {
      status: res.status,
      error: typeof obj.error === "string" ? obj.error : undefined,
      message: typeof obj.error === "string" ? obj.error : `HTTP ${res.status}`,
      details: body,
    };
    throw apiErr;
  }
  return body as T;
}

export function apiPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "PATCH",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "DELETE",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function friendlyError(err: unknown, fallback = "Something went wrong."): string {
  const e = err as ApiError | undefined;
  if (!e) return fallback;
  if (e.status === 429) return "Too many attempts. Please wait a minute and try again.";
  if (e.error === "invalid_credentials") return "Invalid username or password.";
  return e.message || fallback;
}
